import React, { useState } from 'react';
import { Badge, Box, Flex, Text } from '@qga/roo-ui/components';

import { FlightJourney } from '@shared/v2/services/types/api/parsedResponse';

import DepartureArrivalTime from '@/components/DepartureArrivalTime';
import FlightsDetails from '@/components/FlightsDetails';
import FlightsDetailsToggle from '@/components/FlightsDetailsToggle';
import QFIcon from '@/components/QFIcon';
import Skeleton from '@/components/Skeleton';
import formatStops from '@/utils/formatStops';
import getFlightsSashText from '@/utils/getFlightsSashText';
import mapDurationFromFlights from '@/utils/mapDurationFromFlights';

type FlightsSegmentProps = {
  flights: FlightJourney['flightSegments'];
  loading?: boolean;
  direction?: React.ReactNode;
};

const FlightsSegment = ({
  flights,
  loading = false,
  direction,
}: FlightsSegmentProps) => {
  const [expanded, setExpanded] = useState(false);

  const renderTimeCode = () => (
    <>
      <Box fontWeight="bold" mb="1" fontSize="md">
        <Skeleton type="Base" visible={loading}>
          {() => <DepartureArrivalTime flights={flights} />}
        </Skeleton>
      </Box>
      <Box color="greys.steel" fontSize="sm">
        <Skeleton type="Base" visible={loading} width="80px">
          {() => {
            const originCode = flights[0].origin.code;
            const destinationCode =
              flights[flights.length - 1].destination.code;
            return `${originCode} — ${destinationCode}`;
          }}
        </Skeleton>
      </Box>
    </>
  );

  const renderStopDurations = () => (
    <>
      <Box fontWeight="bold" mb="1" fontSize="md">
        <Skeleton type="Base" visible={loading} width="50px">
          {() => formatStops(flights.length)}
        </Skeleton>
      </Box>
      <Box color="greys.steel" fontSize="sm">
        <Skeleton type="Base" visible={loading} width="70px">
          {() => {
            const duration = mapDurationFromFlights(flights);
            return `${duration.hour}h ${duration.minute}m`;
          }}
        </Skeleton>
      </Box>
    </>
  );

  const renderFlightsInfo = () => (
    <Flex>
      <Skeleton type="Base" visible={loading} width="165px">
        {() =>
          flights.map(({ flightNumber, flightClass, destination, origin }) => (
            <Flex
              alignItems="center"
              fontWeight="bold"
              key={`${flightNumber}-${origin?.code}-${destination?.code}`}
              flexDirection="row"
              mr="2"
            >
              {/* @ts-expect-error Icon types inaccurate as not TSX */}
              <QFIcon inline height="0.8em" />
              &nbsp;
              {flightNumber}
              &nbsp;&nbsp;
              <Text color="greys.steel" fontWeight="normal">
                {flightClass}
              </Text>
            </Flex>
          ))
        }
      </Skeleton>
    </Flex>
  );

  const renderFlightsJourney = () => (
    <Flex
      flexDirection={['column', 'row']}
      alignItems={[null, 'center']}
      gap={[4, null, 0]}
    >
      <Flex mr={[null, null, '14']}>
        <Box mr="3" width="130px">
          {renderTimeCode()}
        </Box>
        <Box width="70px">{renderStopDurations()}</Box>
      </Flex>
      {renderFlightsInfo()}
    </Flex>
  );

  const renderSashing = () => {
    if (loading) {
      return null;
    }
    const sashText = getFlightsSashText(flights);
    return sashText ? (
      <Box>
        <Badge text={sashText} />
      </Box>
    ) : null;
  };

  return (
    <Flex
      data-testid="FLIGHT-OPTION"
      flexDirection={['column', null, 'row']}
      gap={4}
    >
      <Flex flexDirection="column" gap={4} width="100%">
        <Flex flexDirection="column" gap={[1, 4]}>
          {renderSashing()}
          <Flex flexDirection={['column', null, 'row']}>
            <Flex
              alignItems={[null, null, 'center']}
              flexDirection={['column', null, 'row']}
              flexWrap="wrap"
              gap={[4, null, 0]}
            >
              {direction && (
                <Flex data-testid="DIRECTION" alignItems="center">
                  {direction}
                </Flex>
              )}
              {renderFlightsJourney()}
            </Flex>
          </Flex>
          {!loading && (
            <Box>
              <FlightsDetailsToggle
                expanded={expanded}
                setExpanded={setExpanded}
                flights={flights}
              />
            </Box>
          )}
        </Flex>
        <FlightsDetails flights={flights} show={expanded} />
      </Flex>
    </Flex>
  );
};

export default FlightsSegment;
